import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  TextField,
  Typography,
  useTheme,
  Paper,
  Alert,
  Tab,
  Tabs,
  Switch,
  FormControlLabel,
  Chip,
  Divider,
} from '@mui/material';
import {
  Save,
  ArrowBack,
  Article,
  Search,
  Share,
  Visibility,
  VisibilityOff,
  Add,
  Close,
} from '@mui/icons-material';
import DashboardHeader from '../../components/DashboardHeader/DashboardHeader';
import Editor from '../../components/Editor';
import 'react-quill/dist/quill.snow.css';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel = ({ children, value, index }: TabPanelProps) => (
  <div hidden={value !== index} style={{ paddingTop: '1.5rem' }}>
    {value === index && children}
  </div>
);

const BlogCreatePost = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);

  // Content fields
  const [title, setTitle] = useState('');
  const [summary, setSummary] = useState('');
  const [content, setContent] = useState('');
  const [files, setFiles] = useState<FileList | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);

  // SEO fields
  const [metaTitle, setMetaTitle] = useState('');
  const [metaDescription, setMetaDescription] = useState('');
  const [customSlug, setCustomSlug] = useState('');
  const [keywords, setKeywords] = useState<string[]>([]);
  const [keywordInput, setKeywordInput] = useState('');
  const [imageAlt, setImageAlt] = useState('');

  // Open Graph fields
  const [ogTitle, setOgTitle] = useState('');
  const [ogDescription, setOgDescription] = useState('');

  // Publishing
  const [isPublished, setIsPublished] = useState(true);

  // Form state
  const [error, setError] = useState<string | null>(null);
  const [emptyFields, setEmptyFields] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const userInfo = useSelector((state: any) => state.global.userInfo);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = e.target.files;
    setFiles(selectedFiles);
    if (selectedFiles && selectedFiles[0]) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setImagePreview(event.target?.result as string);
      };
      reader.readAsDataURL(selectedFiles[0]);
    }
  };

  const handleAddKeyword = () => {
    if (keywordInput.trim() && !keywords.includes(keywordInput.trim())) {
      setKeywords([...keywords, keywordInput.trim()]);
      setKeywordInput('');
    }
  };

  const handleRemoveKeyword = (keywordToRemove: string) => {
    setKeywords(keywords.filter((k) => k !== keywordToRemove));
  };

  const handleKeywordKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddKeyword();
    }
  };

  const createNewPost = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!files || files.length === 0) {
      setError('Nahrajte prosím titulní obrázek');
      setEmptyFields(['file']);
      setActiveTab(0);
      return;
    }

    setIsSubmitting(true);
    const data = new FormData();
    data.set('title', title);
    data.set('summary', summary);
    data.set('content', content);
    data.set('file', files[0]);
    data.set('metaTitle', metaTitle || title);
    data.set('metaDescription', metaDescription || summary);
    data.set('customSlug', customSlug);
    data.set('keywords', keywords.join(','));
    data.set('imageAlt', imageAlt || title);
    data.set('ogTitle', ogTitle || title);
    data.set('ogDescription', ogDescription || summary);
    data.set('isPublished', String(isPublished));

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/post`, {
        method: 'POST',
        body: data,
        credentials: 'include',
      });
      const json = await response.json();
      if (response.ok) {
        setEmptyFields([]);
        setError(null);
        navigate('/blog-admin');
      } else {
        setError(json.error || 'Chyba při vytváření příspěvku');
        setEmptyFields(json.emptyFields ? Object.values(json.emptyFields) : []);
      }
    } catch (err) {
      setError('Nepodařilo se vytvořit příspěvek');
    } finally {
      setIsSubmitting(false);
    }
  };

  const textFieldStyles = {
    '& .MuiOutlinedInput-root': {
      '& fieldset': { borderColor: theme.palette.secondary[300] },
      '&:hover fieldset': { borderColor: theme.palette.secondary[100] },
      '&.Mui-focused fieldset': { borderColor: theme.palette.secondary[100] },
    },
    '& .MuiInputLabel-root': { color: theme.palette.secondary[200] },
    '& .MuiOutlinedInput-input': { color: theme.palette.secondary[100] },
    '& .MuiInputBase-inputMultiline': { color: theme.palette.secondary[100] },
  };

  const editorStyles = {
    '& .ql-toolbar': {
      backgroundColor: theme.palette.primary.light,
      borderColor: theme.palette.secondary[300],
      borderRadius: '4px 4px 0 0',
      '& .ql-stroke': { stroke: theme.palette.secondary[200] },
      '& .ql-fill': { fill: theme.palette.secondary[200] },
      '& .ql-picker': { color: theme.palette.secondary[200] },
      '& .ql-picker-options': {
        backgroundColor: theme.palette.background.alt,
        color: theme.palette.secondary[100],
      },
    },
    '& .ql-container': {
      backgroundColor: theme.palette.primary.light,
      borderColor: theme.palette.secondary[300],
      borderRadius: '0 0 4px 4px',
      minHeight: '400px',
      fontSize: '16px',
    },
    '& .ql-editor': {
      color: theme.palette.secondary[100],
      minHeight: '400px',
      '& h1, & h2, & h3': { color: theme.palette.secondary[100] },
      '& a': { color: theme.palette.secondary[300] },
    },
    '& .ql-editor.ql-blank::before': {
      color: theme.palette.secondary[300],
      fontStyle: 'normal',
    },
  };

  if (!userInfo?.email) {
    return (
      <Box m="1.5rem 2.5rem">
        <Alert severity="warning">Pro vytváření příspěvků je potřeba se přihlásit.</Alert>
      </Box>
    );
  }

  return (
    <Box m="1.5rem 2.5rem">
      <Box display="flex" justifyContent="space-between" alignItems="center" mb="1.5rem">
        <DashboardHeader title="NOVÝ PŘÍSPĚVEK" subtitle="Vytvořte nový blogový příspěvek" />
        <Box display="flex" gap="1rem" alignItems="center">
          <FormControlLabel
            control={
              <Switch
                checked={isPublished}
                onChange={(e) => setIsPublished(e.target.checked)}
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': {
                    color: theme.palette.secondary[300],
                  },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                    backgroundColor: theme.palette.secondary[300],
                  },
                }}
              />
            }
            label={
              <Box display="flex" alignItems="center" gap="0.5rem">
                {isPublished ? <Visibility fontSize="small" /> : <VisibilityOff fontSize="small" />}
                <Typography variant="body2" sx={{ color: theme.palette.secondary[200] }}>
                  {isPublished ? 'Publikovat' : 'Koncept'}
                </Typography>
              </Box>
            }
          />
          <Button
            variant="outlined"
            startIcon={<ArrowBack />}
            onClick={() => navigate('/blog-admin')}
            sx={{
              color: theme.palette.secondary[200],
              borderColor: theme.palette.secondary[200],
            }}
          >
            Zpět
          </Button>
        </Box>
      </Box>

      <Paper
        component="form"
        onSubmit={createNewPost}
        sx={{
          backgroundColor: theme.palette.background.alt,
          borderRadius: '0.55rem',
          overflow: 'hidden',
        }}
      >
        {error && (
          <Alert severity="error" sx={{ m: '1rem' }}>
            {error}
            {emptyFields.length > 0 && `: ${emptyFields.join(', ')}`}
          </Alert>
        )}

        <Tabs
          value={activeTab}
          onChange={(_, newValue) => setActiveTab(newValue)}
          sx={{
            borderBottom: `1px solid ${theme.palette.secondary[300]}`,
            '& .MuiTab-root': { color: theme.palette.secondary[200] },
            '& .Mui-selected': { color: theme.palette.secondary[100] },
            '& .MuiTabs-indicator': { backgroundColor: theme.palette.secondary[300] },
          }}
        >
          <Tab icon={<Article />} label="Obsah" iconPosition="start" />
          <Tab icon={<Search />} label="SEO" iconPosition="start" />
          <Tab icon={<Share />} label="Sociální sítě" iconPosition="start" />
        </Tabs>

        <Box p="1.5rem">
          {/* Content Tab */}
          <TabPanel value={activeTab} index={0}>
            <Box display="flex" flexDirection="column" gap="1.5rem">
              <TextField
                label="Název příspěvku"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                fullWidth
                required
                error={emptyFields.includes('title')}
                sx={textFieldStyles}
              />

              <TextField
                label="Shrnutí (zobrazí se v náhledu)"
                value={summary}
                onChange={(e) => setSummary(e.target.value)}
                fullWidth
                required
                multiline
                rows={3}
                error={emptyFields.includes('summary')}
                helperText={`${summary.length}/300 znaků doporučeno`}
                sx={textFieldStyles}
              />

              <Box>
                <Typography
                  variant="subtitle2"
                  sx={{ color: theme.palette.secondary[200], mb: '0.5rem' }}
                >
                  Titulní obrázek *
                </Typography>
                <Box
                  sx={{
                    border: `2px dashed ${emptyFields.includes('file') ? 'red' : theme.palette.secondary[300]}`,
                    borderRadius: '8px',
                    p: '1.5rem',
                    textAlign: 'center',
                    cursor: 'pointer',
                    transition: 'border-color 0.2s',
                    '&:hover': { borderColor: theme.palette.secondary[100] },
                  }}
                  onClick={() => document.getElementById('file-input')?.click()}
                >
                  {imagePreview ? (
                    <Box>
                      <Box
                        component="img"
                        src={imagePreview}
                        alt="Preview"
                        sx={{
                          maxWidth: '100%',
                          maxHeight: '300px',
                          objectFit: 'contain',
                          borderRadius: '4px',
                          mb: '1rem',
                        }}
                      />
                      <Typography variant="body2" sx={{ color: theme.palette.secondary[200] }}>
                        Klikněte pro změnu obrázku
                      </Typography>
                    </Box>
                  ) : (
                    <Box>
                      <Add
                        sx={{ fontSize: 48, color: theme.palette.secondary[300], mb: '0.5rem' }}
                      />
                      <Typography variant="body1" sx={{ color: theme.palette.secondary[200] }}>
                        Klikněte nebo přetáhněte obrázek
                      </Typography>
                      <Typography variant="caption" sx={{ color: theme.palette.secondary[300] }}>
                        Doporučený poměr 16:9, min. 1200x675px
                      </Typography>
                    </Box>
                  )}
                  <input
                    id="file-input"
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                    style={{ display: 'none' }}
                  />
                </Box>
              </Box>

              <Box>
                <Typography
                  variant="subtitle2"
                  sx={{ color: theme.palette.secondary[200], mb: '0.5rem' }}
                >
                  Obsah příspěvku *
                </Typography>
                <Box sx={editorStyles}>
                  <Editor onChange={setContent} value={content} />
                </Box>
              </Box>
            </Box>
          </TabPanel>

          {/* SEO Tab */}
          <TabPanel value={activeTab} index={1}>
            <Box display="flex" flexDirection="column" gap="1.5rem">
              <Alert severity="info" sx={{ mb: '1rem' }}>
                SEO pole pomáhají vyhledávačům lépe pochopit obsah vašeho příspěvku. Pokud je
                necháte prázdná, použijí se hodnoty z hlavního obsahu.
              </Alert>

              <TextField
                label="Meta titulek (pro vyhledávače)"
                value={metaTitle}
                onChange={(e) => setMetaTitle(e.target.value)}
                fullWidth
                placeholder={title || 'Použije se název příspěvku'}
                helperText={`${(metaTitle || title).length}/60 znaků (doporučeno 50-60)`}
                sx={textFieldStyles}
              />

              <TextField
                label="Meta popis (pro vyhledávače)"
                value={metaDescription}
                onChange={(e) => setMetaDescription(e.target.value)}
                fullWidth
                multiline
                rows={3}
                placeholder={summary || 'Použije se shrnutí'}
                helperText={`${(metaDescription || summary).length}/160 znaků (doporučeno 150-160)`}
                sx={textFieldStyles}
              />

              <TextField
                label="URL slug (volitelné)"
                value={customSlug}
                onChange={(e) => setCustomSlug(e.target.value)}
                fullWidth
                placeholder="muj-vlastni-url"
                helperText="Vlastní URL adresa příspěvku. Pokud necháte prázdné, vytvoří se z názvu."
                sx={textFieldStyles}
              />

              <Box>
                <Typography
                  variant="subtitle2"
                  sx={{ color: theme.palette.secondary[200], mb: '0.5rem' }}
                >
                  Klíčová slova (tagy)
                </Typography>
                <Box display="flex" gap="0.5rem" mb="0.5rem">
                  <TextField
                    value={keywordInput}
                    onChange={(e) => setKeywordInput(e.target.value)}
                    onKeyPress={handleKeywordKeyPress}
                    placeholder="Přidejte klíčové slovo"
                    size="small"
                    fullWidth
                    sx={textFieldStyles}
                  />
                  <Button
                    variant="outlined"
                    onClick={handleAddKeyword}
                    sx={{
                      color: theme.palette.secondary[200],
                      borderColor: theme.palette.secondary[200],
                    }}
                  >
                    Přidat
                  </Button>
                </Box>
                <Box display="flex" flexWrap="wrap" gap="0.5rem">
                  {keywords.map((keyword) => (
                    <Chip
                      key={keyword}
                      label={keyword}
                      onDelete={() => handleRemoveKeyword(keyword)}
                      deleteIcon={<Close />}
                      sx={{
                        backgroundColor: theme.palette.secondary[300],
                        color: theme.palette.primary[600],
                        '& .MuiChip-deleteIcon': { color: theme.palette.primary[600] },
                      }}
                    />
                  ))}
                </Box>
              </Box>

              <TextField
                label="Alt text obrázku"
                value={imageAlt}
                onChange={(e) => setImageAlt(e.target.value)}
                fullWidth
                placeholder={title || 'Použije se název příspěvku'}
                helperText="Popis obrázku pro vyhledávače a čtečky obrazovky"
                sx={textFieldStyles}
              />

              <Divider sx={{ borderColor: theme.palette.secondary[300], my: '1rem' }} />

              <Box
                sx={{
                  backgroundColor: theme.palette.primary.light,
                  p: '1rem',
                  borderRadius: '8px',
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{ color: theme.palette.secondary[100], mb: '0.5rem' }}
                >
                  Náhled ve vyhledávači
                </Typography>
                <Box>
                  <Typography
                    variant="body1"
                    sx={{ color: '#1a0dab', fontWeight: 500, mb: '0.25rem' }}
                  >
                    {metaTitle || title || 'Název příspěvku'}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#006621', mb: '0.25rem' }}>
                    smartpush.cz/blog/
                    {customSlug ||
                      (title ? title.toLowerCase().replace(/\s+/g, '-') : 'url-prispevku')}
                  </Typography>
                  <Typography variant="body2" sx={{ color: theme.palette.secondary[300] }}>
                    {(metaDescription || summary || 'Popis příspěvku...').substring(0, 160)}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </TabPanel>

          {/* Social Tab */}
          <TabPanel value={activeTab} index={2}>
            <Box display="flex" flexDirection="column" gap="1.5rem">
              <Alert severity="info" sx={{ mb: '1rem' }}>
                Open Graph data se zobrazí při sdílení na sociálních sítích (Facebook, LinkedIn,
                Twitter). Pokud je necháte prázdná, použijí se SEO hodnoty.
              </Alert>

              <TextField
                label="Titulek pro sociální sítě"
                value={ogTitle}
                onChange={(e) => setOgTitle(e.target.value)}
                fullWidth
                placeholder={metaTitle || title || 'Použije se meta titulek'}
                sx={textFieldStyles}
              />

              <TextField
                label="Popis pro sociální sítě"
                value={ogDescription}
                onChange={(e) => setOgDescription(e.target.value)}
                fullWidth
                multiline
                rows={3}
                placeholder={metaDescription || summary || 'Použije se meta popis'}
                sx={textFieldStyles}
              />

              <Box
                sx={{
                  backgroundColor: theme.palette.primary.light,
                  p: '1rem',
                  borderRadius: '8px',
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{ color: theme.palette.secondary[100], mb: '1rem' }}
                >
                  Náhled při sdílení
                </Typography>
                <Box
                  sx={{
                    border: `1px solid ${theme.palette.secondary[300]}`,
                    borderRadius: '8px',
                    overflow: 'hidden',
                    maxWidth: '500px',
                  }}
                >
                  {imagePreview && (
                    <Box
                      component="img"
                      src={imagePreview}
                      alt="Preview"
                      sx={{ width: '100%', height: '260px', objectFit: 'cover' }}
                    />
                  )}
                  <Box p="1rem" sx={{ backgroundColor: theme.palette.background.alt }}>
                    <Typography variant="caption" sx={{ color: theme.palette.secondary[300] }}>
                      smartpush.cz
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{ color: theme.palette.secondary[100], fontWeight: 600, my: '0.25rem' }}
                    >
                      {ogTitle || metaTitle || title || 'Název příspěvku'}
                    </Typography>
                    <Typography variant="body2" sx={{ color: theme.palette.secondary[200] }}>
                      {(
                        ogDescription ||
                        metaDescription ||
                        summary ||
                        'Popis příspěvku...'
                      ).substring(0, 100)}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>
          </TabPanel>

          <Box
            display="flex"
            justifyContent="flex-end"
            mt="2rem"
            pt="1.5rem"
            borderTop={`1px solid ${theme.palette.secondary[300]}`}
          >
            <Button
              type="submit"
              variant="contained"
              disabled={isSubmitting}
              startIcon={<Save />}
              size="large"
              sx={{
                backgroundColor: theme.palette.secondary[300],
                color: theme.palette.primary[600],
                px: '2rem',
                '&:hover': { backgroundColor: theme.palette.secondary[100] },
                '&:disabled': { backgroundColor: theme.palette.secondary[500] },
              }}
            >
              {isSubmitting ? 'Ukládám...' : isPublished ? 'Publikovat' : 'Uložit koncept'}
            </Button>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

export default BlogCreatePost;
